// frontend/src/admin/v2/AdminLayout.jsx
import { useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation, Link as RouterLink } from 'react-router-dom'
import {
  Box, Button, Tabs, Tab, Typography, useMediaQuery, useTheme,
  Menu, MenuItem, Divider,
} from '@mui/material'
import AppsIcon from '@mui/icons-material/Apps'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckIcon from '@mui/icons-material/Check'
import PageContainer from '../../components/common/PageContainer'
import ViewHeader from '../../components/ViewHeader'
import { SP } from '../../theme/tokens'
import { scrollBehavior } from '../../utils/scroll'
import StatusPill from './components/StatusPill'
import { useDashboardSummary } from './hooks/useDashboardSummary'

/**
 * AdminLayout — shell for the new /admin/* mini-app.
 *
 * @description
 *   Header rendered through the shared `ViewHeader` — breadcrumb "Admin ›
 *   <section>" (first crumb back to /admin), the SECTION NAME as the h1 and
 *   the status pill as the right-hand header action. Until 2026-10-06 the
 *   header was a local Stack with a constant "Admin" h1 and no top padding, so
 *   the admin area sat out of the rhythm of every other page (R16 W1/W4).
 *   Section nav below: the eight primary sections as tabs plus one "More" tab
 *   that opens a menu with the five utility surfaces (desktop), and one
 *   navigation selector on mobile. Outlet renders the active section's page.
 *   Status pill polls every 30s via useDashboardSummary so it always reflects
 *   current health.
 *
 * @useWhen
 *   - Wraps every /admin/* route. Don't render outside the admin area.
 *
 * @related
 *   - useDashboardSummary — drives the status pill
 *   - StatusPill — header indicator
 *
 * 🔴 **Owner-Entscheid UX4, 2026-10-28, Variante A: Routenreiter sind keine
 * Inhaltsreiter.** Der Umbau auf EINE `UnderlineTabs`-Reihe mit dreizehn
 * Reitern (G14a-0, 2026-10-28) ist damit zurückgenommen: `UnderlineTabs` ist
 * ein Inhaltsreiter-Baustein, ihre JSDoc und der Registry-Eintrag schließen
 * Routen-Navigation ausdrücklich aus (Konzept §5.4: „die Seitennavigation
 * (Routenreiter) — beide sind keine Inhaltsreiter"), und die rollende Reihe
 * hatte keine sichtbare Scroll-Vorrichtung (Regel-16-B1: „Privacy" und
 * „Settings" waren bei 1280 px mit der Maus nicht erreichbar). Der
 * Navigations-Auswähler auf dem Telefon bleibt aus G14a-0 bestehen (N5.6/P31).
 *
 *   Damit ist `UnderlineTabs.jsx`, ihr Registry-Eintrag und das Konzept
 *   unverändert richtig — sie beschreiben jetzt wieder genau das, was im Baum
 *   steht. Die Kollision N5.5 („mehr als sieben Reiter oder Routenreiter →
 *   Navigations-Auswähler") gegen P30 („eine Reiterform") ist mit diesem
 *   Entscheid zugunsten der Ausnahme in §5.4 aufgelöst: für Routen gilt der
 *   Navigations-Auswähler unter `md`, oberhalb `md` die Reiterreihe mit
 *   Überlauf-Menü.
 */
export const SECTIONS = [ // exportiert fuer adminLabels.test.jsx (R16-B W-2)
  // 🔴 G14a-0 (2026-10-28): `width` je Abschnitt. Das Breitenkonzept hat die
  // Achse ausdruecklich der Karte ueberlassen (`concepts/inhaltsbreite.md:339`,
  // `AdminLayout.jsx` Docstring bis G0b.5) — G14 entscheidet sie hier: `full`
  // fuer die Kartenraster und die zwei Tabellen mit fuenf und mehr Spalten
  // (`jobs`, `integrations`, `cron-failures`, `audit`, `email-log`), `reading`
  // fuer alles, was eine Lesespalte ist (Kacheln, Zeilenlisten, Textkarten).
  // Die Vorgabe bleibt `reading` (1280) — der Rueckfall in `AdminLayout`.
  { key: 'dashboard',     label: 'Dashboard',    path: '/admin',                     width: 'reading' },
  { key: 'integrations',  label: 'Integrations', path: '/admin/integrations',        width: 'full' },
  { key: 'jobs',          label: 'Jobs',         path: '/admin/jobs',                width: 'full' },
  { key: 'audit',         label: 'Audit',        path: '/admin/audit',               width: 'full' },
  { key: 'secrets',       label: 'Secrets',      path: '/admin/secrets',             width: 'reading' },
  { key: 'notifications', label: 'Notifications', path: '/admin/notifications',      width: 'reading' },
  { key: 'cron-failures', label: 'Cron Failures', path: '/admin/cron-failures',      width: 'full' },
  { key: 'docs',          label: 'Docs',         path: '/admin/docs',                width: 'reading' },
  // 🔴 C4 (Owner-Entscheid, 2026-09-28): drei Flaechen, die bisher NUR als
  // Route existierten — kein Menuepunkt, kein Link. Hier ist ihre Sektionsliste,
  // also gehoeren sie hierher.
  //
  // 🔴 W10/W-4 der Gates zu PR 4: die vier neuen Eintraege standen auf Deutsch
  // in einer sonst durchgehend englischen Liste ("Notifications", "Cron
  // Failures", "Docs", "Settings"). Die Liste ist englisch, also sind es diese
  // vier auch — es gibt keine Sprachrichtungsentscheidung fuer den
  // Admin-Bereich, nur eine Liste.
  { key: 'email-log',     label: 'Email log',   path: '/admin/email-log', secondary: true, width: 'full' },
  { key: 'notification-tester', label: 'Test notifications', path: '/admin/notification-tester', secondary: true, width: 'reading' },
  { key: 'spare-parts-review',  label: 'Spare parts review',  path: '/admin/spare-parts-review', secondary: true, width: 'reading' },
  // Privacy: der EINE Eingang fuer beide Werkzeuge (Antragsschlange und
  // manuelles Loeschen). `gdpr-wipe` ist von dort verlinkt, nicht eigenstaendig.
  { key: 'privacy',       label: 'Privacy',  path: '/admin/privacy', secondary: true, width: 'reading' },
  { key: 'settings',      label: 'Settings',     path: '/admin/settings', secondary: true, width: 'reading' },
]

// 🔴 UX4 (2026-10-28, Variante A): `secondary` ist wieder eine
// Navigationsverzweigung — die fuenf Werkzeugflaechen liegen hinter dem
// „More"-Reiter, nicht in der Reihe. G14a-0 hatte sie in die Reihe geholt
// (P30); mit dem Entscheid ist P30 fuer Admin-Routen nicht mehr der Maßstab,
// weil Routen-Reiter keine Inhaltsreiter sind (Konzept §5.4).
const MORE_KEY = '__more__'
const PRIMARY_SECTIONS = SECTIONS.filter(s => !s.secondary)
const SECONDARY_SECTIONS = SECTIONS.filter(s => s.secondary)

function pickActive(pathname) {
  // Longest-prefix wins so /admin/jobs/<key> still highlights "Jobs".
  const ordered = [...SECTIONS].sort((a, b) => b.path.length - a.path.length)
  return ordered.find(s => pathname === s.path || pathname.startsWith(s.path + '/'))?.key
       || 'dashboard'
}

/**
 * AdminLayout — die gemeinsame Schale aller `/admin/*`-Seiten.
 *
 * 🔴 G0b.5 (2026-10-13), Konzept §6.2 Ziff. 9: „`AdminLayout` gibt sein Muster
 * weiter." Der Rahmen reicht sein `width` an den `PageContainer` — und damit
 * ueber `PageWidthContext` an Kopf **und** jede Tabelle im `<Outlet/>` (die zwei
 * sitzen in **einem** Container, der Kanal kommt also an).
 *
 * 🔴 **Seit G14a-0 (2026-10-28) kommt die Breite aus dem AKTIVEN Abschnitt**
 * (`SECTIONS[].width`, Rueckfall `reading`). Das Prop `width` bleibt als
 * ausdrueckliche Uebersteuerung bestehen — ohne Aufrufer ist es wirkungslos,
 * weil kein Abschnitt ohne `width` dasteht.
 *
 * 🔴 **Navigation (UX4, 2026-10-28, Variante A):** acht Hauptabschnitte als
 * Reiter plus „More ⌄" fuer die fuenf Werkzeugflaechen. Liegt der aktuelle
 * Abschnitt unter „More", traegt der Reiter SEINEN Namen („Privacy ⌄") und ist
 * ausgewaehlt — sonst waere der aktuelle Ort auf dem Desktop unsichtbar.
 * Mobil (unter `md`) traegt ein Auswahl-Knopf „Admin · <Abschnitt>" mit
 * Navigationssymbol das Menue — **kein** `Select`, damit die Navigation nicht
 * wie ein Filter aussieht (P31/N5.6).
 *
 * 🔴 **Die Reiter sind echte Links** (`component={RouterLink}`, `to`), der
 * aktuelle traegt zusaetzlich `aria-current="page"`; Strg/Cmd-Klick oeffnet
 * einen neuen Tab, das Kontextmenue des Browsers funktioniert. Das ist der
 * Unterschied zu G14a-0, wo die Reiter `div role="tab"` ohne Ziel waren.
 * Der `role="tab"` bleibt trotzdem: `Tabs`/`Tab` sind die Reiterform, und die
 * Pfeiltasten-Steuerung haengt daran. Der Preis ist benannt — ein Link mit
 * `role="tab"` meldet sich Screenreadern als Reiter, nicht als Link.
 *
 * 🔴 **Der zweite Teil des Preises** (R16-W1, 2026-10-29): es gibt **kein**
 * `role="tabpanel"` und **kein** `aria-controls`, weil der Inhalt eine ROUTE
 * ist und kein Panel — der Reiter steuert nichts, er navigiert. Und der
 * Roving-`tabindex` von `Tabs` nimmt die uebrigen Reiter aus der
 * Tab-Reihenfolge: erreichbar sind sie mit den Pfeiltasten, nicht mit `Tab`.
 * Das ist der Gegenwert der Reiterform (eine Tab-Stelle statt neun) und
 * zugleich eine Bedienentscheidung, die benannt gehoert. Damit ein
 * Screenreader die Reihe einordnen kann, traegt sie einen zugaenglichen Namen
 * („Admin sections", R16-C1).
 *
 * 🔴 **Der Inhalt haengt EINMAL im Baum.** Bis zur Nachbesserung 2 stand
 * `<Outlet/>` zweimal (einmal im Paneel, einmal im Mobil-Zweig); ein Wechsel
 * ueber `md` — iPad hoch/quer — hing den Teilbaum aus und neu ein, und jeder
 * lokale Zustand der Seite (Filter, Suchtext, Blaetterposition) war weg
 * (Regel-15-Bedingung 1). Jetzt steht er hinter der Navigation, ausserhalb
 * jeder breakpointabhaengigen Verzweigung; `AdminLayout.test.jsx` misst das
 * mit einem Einhaenge-Zaehler.
 */
export default function AdminLayout({ width: widthOverride }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()
  const { data: summary } = useDashboardSummary()
  const active = useMemo(() => pickActive(location.pathname), [location.pathname])
  const [moreAnchor, setMoreAnchor] = useState(null)
  const [navAnchor, setNavAnchor] = useState(null)

  // 🔴 R15-B1 (2026-10-29, Nachbesserung 4): der Effekt stand auf `master`
  // (`origin/master:…/AdminLayout.jsx:119-124`) und ist beim UX4-Umbau
  // mitgeloescht worden — ein stiller Rueckschritt, den die Commit-Botschaft
  // nicht nannte. Er kommt hier zurueck; die Begruendung ist woertlich von
  // dort: „MUI scrolls the selected tab into view itself, but on a cold
  // deep-link it can measure before the layout has settled and leave the
  // active tab off the left edge. Scroll once after mount, and again whenever
  // the tab changes."
  //
  // 🔴 NB5 (2026-10-29, Nachbesserung 5): `scrollIntoView` allein trug nicht.
  // MUI holt den gewaehlten Reiter zwar selbst herein
  // (`@mui/material/Tabs/Tabs.js:538 scrollSelectedIntoView`), misst aber,
  // BEVOR die Rollknoepfe da sind: die entstehen erst nach dem ersten Bild
  // (`IntersectionObserver`, `Tabs.js:621-647`), und danach ist der Rollbereich
  // um ihre Breite schmaler. MUI misst nicht nach — sein Nachlauf haengt am
  // Indikator (`Tabs.js:654`), und der aendert sich nicht, weil Reiter und
  // Rollbereich gemeinsam wandern.
  //
  // 🔴 NB6 (2026-10-30): auch der Nachlauf aus NB5 trug im Browser nicht, und
  // der Grund liegt eine Ebene tiefer. MUIs eigener Lauf ist ANIMIERT
  // (`Tabs.js:435-445` ruft `internal/animate.js`): `animate` schreibt
  // `scrollLeft` in JEDEM Bild neu (Zeile 30 dort) auf ein Ziel, das es VOR
  // dem Erscheinen der Rollknoepfe berechnet hat (`Tabs.js:546-558`), und MUI
  // verwirft die Abbruchfunktion (`Tabs.js:439`). Eine Korrektur aus dem
  // `ResizeObserver` wird damit im naechsten Bild ueberschrieben; danach
  // schreibt niemand mehr, und der Reiter bleibt stehen.
  //
  // Am Kopf gemessen (960 und 1024, kalter Deep-Link auf /admin/privacy, Foto
  // `960_unter-more.jpg`): der Reiter endete 80,1 px hinter der rechten Kante
  // des Rollbereichs — genau die zwei Rollknoepfe (2 × 40 px). Seine rechte
  // Kante lag auf `linke Kante des Rollbereichs MIT Knopf + Breite des
  // Rollbereichs OHNE Knoepfe` (956,109375 = 84 + 872,109375; 1020,109375 =
  // 84 + 936,109375) — also exakt auf dem Maximum des BREITEN Kastens. Und der
  // rechte Pfeil stand ueberhaupt nur da, weil der Reiter beim ersten Bild des
  // `IntersectionObserver` noch angeschnitten war; ein abgeschlossener,
  // synchroner Lauf haette ihn zu 100 % sichtbar gemacht und den Pfeil nie
  // entstehen lassen. Also war eine Animation unterwegs, als die Knoepfe kamen.
  //
  // Deshalb misst dieser Effekt gegen den SICHTBAREN Rollbereich (den
  // Elternteil der `role="tablist"`, MUI-Struktur `Tabs.js:782-795`) und
  // laesst die Korrektur ein FENSTER lang mitlaufen — so lange, wie MUIs
  // Animation hoechstens dauern kann (`theme.transitions.duration.standard`,
  // derselbe Wert, den `Tabs.js:440` benutzt). Schreibt zwischen zwei Bildern
  // jemand anderes — MUIs naechstes Bild oder ein spaeter nachgezogener Lauf —,
  // verlaengert sich das Fenster, und die letzte Schreibung ist unsere.
  //
  // `scrollIntoView` waere der naheliegende Ruf, ist aber der falsche Hebel:
  // es zentriert und bewegt notfalls auch Vorfahren, statt den Rollbereich
  // genau um den Ueberstand zu schieben.
  //
  // 🔴 R16-C1 (2026-10-30, Regel-16-Runde 3): der Nachlauf ist BEWEGUNG, und
  // er fragt die Praeferenz ab. Hausnorm `frontend/src/utils/scroll.js:10-12`
  // — der Browser schaltet sie nicht von selbst ab, jede Aufrufstelle braucht
  // die Abfrage. `scrollBehavior()` antwortet `'auto'` bei reduzierter
  // Bewegung UND wenn `matchMedia` fehlt (dann ist „nicht bewegen" die sichere
  // Antwort). Bei `'auto'` laeuft KEINE Bildschleife: eine Korrektur am
  // Fensterende, und bei einer fremden Schreibung eine zweite — begrenzt auf
  // zwei Fenster (R15-W1, 2026-10-31). Der Reiter steht danach trotzdem im
  // Rollbereich. MUIs EIGENE Animation laeuft dabei weiter (die Hausregel
  // `theme.js` erreicht sie nicht, sie ist JS): 200 ms Mitlauf, danach ggf.
  // der Sprung — der Preis der Loesung, benannt in der Feature-Doku.
  //
  // Mobil gibt es keine Reihe (`tabsRef.current` ist dann null).
  const tabsRef = useRef(null)
  useEffect(() => {
    const wurzel = tabsRef.current
    const liste = wurzel?.querySelector('[role="tablist"]')
    // Der Rollbereich ist der SCROLLER, nicht die Liste: die Liste wandert mit
    // `scrollLeft` mit, ihre Kante sagt also nichts ueber „sichtbar".
    const rollbereich = liste?.parentElement
    if (!wurzel || !liste || !rollbereich) return undefined

    const einholen = () => {
      const gewaehlt = wurzel.querySelector('[aria-selected="true"]')
      if (!gewaehlt) return
      const sichtbar = rollbereich.getBoundingClientRect()
      const kasten = gewaehlt.getBoundingClientRect()
      if (kasten.left < sichtbar.left) {
        rollbereich.scrollLeft -= sichtbar.left - kasten.left
      } else if (kasten.right > sichtbar.right) {
        rollbereich.scrollLeft += kasten.right - sichtbar.right
      }
    }

    // Das Fenster: so lange kann MUIs Animation hoechstens laufen. Kein
    // Literal — es ist derselbe Theme-Wert, den `Tabs.js:440` liest.
    const fenster = theme.transitions.duration.standard
    // R16-C1: bei reduzierter Bewegung gibt es keine Bildschleife, sondern
    // eine Korrektur am Fensterende — und bei fremder Schreibung eine
    // weitere, begrenzt (`nachziehen`, hoechstens zwei Fenster).
    const reduziert = scrollBehavior() === 'auto'
    let bild = 0
    let nachlaufTimer = null
    let bis = 0
    let unserWert = null
    let abgebrochen = false
    // R16-W3: bis wann der Abbruch gilt (Ende des laufenden Fensters).
    let abgebrochenBis = 0

    const nachlauf = () => {
      bild = 0
      if (abgebrochen) return
      // Hat zwischen zwei Bildern jemand anderes geschrieben (MUIs Animation),
      // laeuft das Fenster weiter — die letzte Schreibung muss unsere sein.
      const fremd = unserWert !== null && rollbereich.scrollLeft !== unserWert
      einholen()
      unserWert = rollbereich.scrollLeft
      if (fremd) bis = performance.now() + fenster
      if (performance.now() < bis) bild = requestAnimationFrame(nachlauf)
    }

    // 🔴 R15-W1 (2026-10-31, Runde 4): der reduzierte Zweig kann das Rennen
    // gegen MUIs LETZTES Bild verlieren. MUI schreibt in dem Bild, in dem
    // `elapsed >= duration` — bei 16,7-ms-Raster also bei 200,4 ms (gewonnen)
    // oder erst bei 216,7 ms (verloren); unsere Korrektur sitzt bei
    // `fenster + 16`. Deshalb wird nach jeder Korrektur geprueft, ob inzwischen
    // jemand anderes geschrieben hat: dann EINE weitere Korrektur samt Fenster,
    // begrenzt auf ZWEI Fenster. Kein `requestAnimationFrame` — das ist der
    // ganze Unterschied bei reduzierter Bewegung, und die Grenze haelt den
    // Nachlauf endlich (kein Dauerlauf).
    const nachziehen = (rest) => {
      const fremd = unserWert !== null && rollbereich.scrollLeft !== unserWert
      einholen()
      unserWert = rollbereich.scrollLeft
      if (!fremd || rest <= 0) return
      nachlaufTimer = setTimeout(() => {
        nachlaufTimer = null
        if (abgebrochen) return
        nachziehen(rest - 1)
      }, fenster + 16)
    }

    const anstossen = (vomBeobachter) => {
      if (abgebrochen) {
        // 🔴 R16-W3 (2026-10-31, Runde 4): der Abbruch gilt fuer das LAUFENDE
        // Fenster. Innerhalb davon hat der Nutzer Vorrang — ein Pfeilklick
        // darf nicht von einem Resize im selben Fenster ueberschrieben werden.
        // Danach schaerft ein spaeterer Beobachteraufruf wieder; sonst braechte
        // eine nachgeladene Schrift den gewaehlten Reiter nie mehr herein.
        if (!vomBeobachter || performance.now() < abgebrochenBis) return
        abgebrochen = false
      }
      einholen()
      unserWert = rollbereich.scrollLeft
      bis = performance.now() + fenster
      if (reduziert) {
        // Eine Korrektur am Fensterende — und bei fremder Schreibung eine
        // zweite (`nachziehen`), dann ist Schluss.
        if (nachlaufTimer) clearTimeout(nachlaufTimer)
        nachlaufTimer = setTimeout(() => {
          nachlaufTimer = null
          if (abgebrochen) return
          nachziehen(1)
        }, fenster + 16)
        return
      }
      if (!bild) bild = requestAnimationFrame(nachlauf)
    }

    // Der Nutzer hat Vorrang: eine waagerechte Radgeste, ein Zeiger oder eine
    // Taste auf der Reihe beendet den Nachlauf. Ein senkrechtes Mausrad rollt
    // die SEITE, nicht die Reihe, und zaehlt nicht.
    //
    // 🔴 R15-W4 (2026-10-30, Regel-15-Runde 3): die reine Senkrecht-Erkennung
    // (`|deltaY| > |deltaX|`) liess eine DIAGONALE Geste durch — der Nachlauf
    // lief dann bis zu ein Fenster lang gegen den Nutzer. Deshalb gilt
    // zusaetzlich jede waagerechte Komponente ueber einer Schwelle als Geste
    // an der Reihe. Die Schwelle trennt Absicht von Zittern: ein senkrechtes
    // Trackpad-Rad erzeugt 0–2 px `deltaX`, eine diagonale Wischgeste liegt
    // weit darueber.
    const WAAGERECHT_PX = 4
    const nutzerRollt = (ereignis) => {
      if (ereignis && ereignis.type === 'wheel'
        && Math.abs(ereignis.deltaY) > Math.abs(ereignis.deltaX)
        && Math.abs(ereignis.deltaX) <= WAAGERECHT_PX) return
      abgebrochen = true
      abgebrochenBis = performance.now() + fenster
      if (bild) {
        cancelAnimationFrame(bild)
        bild = 0
      }
      if (nachlaufTimer) {
        clearTimeout(nachlaufTimer)
        nachlaufTimer = null
      }
    }
    // 🔴 R15-W2 (2026-10-30, Regel-15-Runde 3): die Zuhörer hängen an der
    // `Tabs`-WURZEL, nicht am Rollbereich. MUIs Rollknöpfe sind GESCHWISTER des
    // Rollbereichs (`Tabs.js:782-795`) — ein Klick auf einen Pfeil traf ihn
    // also nicht, und der Nachlauf überschrieb MUIs Lauf: der Pfeil „wirkte
    // nicht". Die Wurzel enthält beide.
    const bedienarten = ['wheel', 'pointerdown', 'keydown']
    bedienarten.forEach(art => wurzel.addEventListener(art, nutzerRollt, { passive: true }))

    anstossen()

    // jsdom kennt keinen echten `ResizeObserver` (der Stummel in
    // `setupTests.js` ist ein No-op); dort bleibt es beim einen Aufruf und
    // seinem Fenster.
    let beobachter = null
    if (typeof ResizeObserver !== 'undefined') {
      beobachter = new ResizeObserver(() => anstossen(true))
      beobachter.observe(rollbereich)
      // Die Reiter selbst auch: „More" heisst bei einem Werkzeug-Abschnitt
      // „Privacy" und wird breiter, und eine nachgeladene Schrift verschiebt
      // jede Breite — beides aendert die Lage des gewaehlten Reiters, ohne dass
      // der Rollbereich seine Breite aendert.
      Array.from(liste.children).forEach(reiter => beobachter.observe(reiter))
    }
    return () => {
      bedienarten.forEach(art => wurzel.removeEventListener(art, nutzerRollt))
      if (beobachter) beobachter.disconnect()
      if (bild) cancelAnimationFrame(bild)
      if (nachlaufTimer) clearTimeout(nachlaufTimer)
    }
  }, [active, isMobile, theme])
  const section = SECTIONS.find(s => s.key === active)
  const sectionLabel = section?.label || ''
  const secondaryActive = Boolean(section?.secondary)
  const width = widthOverride || section?.width || 'reading'
  // ViewHeader sizes its own title per breakpoint (`h5` desktop / `h6` mobile).
  // The title is passed as an element so it can be a real `<h1>`; the size has
  // to follow the same split by hand.
  //
  // 🔴 Welle 2 PR 2 Teil B (2026-10-06): `narrow` (unter `sm`, 600 px) treibt
  // jetzt AUCH den Status-Pill, nicht mehr `isMobile` (unter `md`, 900 px).
  // Dazwischen — 600 bis 900 px — ist der Pill NICHT mehr kompakt: im Gate zu
  // Teil A am 768-px-Kopf gemessen blieben rechts mehr als 300 px frei, der
  // Zustandstext passt also sichtbar hinein. Kompakt (Punkt + versteckter Text,
  // nur ueber `title` und den Screenreader lesbar) bleibt er unter `sm`, wo der
  // Kopf wirklich eng ist.
  const narrow = useMediaQuery(theme.breakpoints.down('sm'))

  // 🔴 R16-W2 (2026-10-29, Nachbesserung 3): Kopf und Navigationszeile teilen
  // DIESELBE linke Kante — und zwar STRUKTURELL: die Zeile haengt im
  // `children`-Slot von `ViewHeader` und damit in genau dem Kasten, der den
  // Titel traegt. Sie rechnet keine Kante und keinen Deckel mehr selbst aus.
  //
  // 🔴 Warum die Vorfassung (2026-10-28) nicht trug — am Foto gemessen
  // (`1920_grund.png`, Pixelspalten): bei 1920 ist der Seitenkasten **1592 px
  // breit und beginnt bei x=284** (Leiste 240 + Inhaltszelle `px: 2.5` = 20 +
  // `PAGE_GUTTER` 24). `ViewHeader` deckelt auf 1280 und zentriert: Titel bei
  // 284 + (1592 − 1280)/2 = **440**. Die Zeile stand als GESCHWISTER im
  // `Stack` und setzte `maxWidth: 1280, mx: 'auto'` selbst — im Spalten-Flex
  // des `Stack` zentriert `mx: 'auto'` bei `width: 100%` aber NICHT: die
  // Trennlinie der Reiterzeile beginnt im Foto bei **x=284**, nicht bei 440
  // (Ende 1563 = 284 + 1279). Der Browserlauf meldete die erste Reiterbox bei
  // 324; die 40 px dazwischen sind am Foto NICHT aufloesbar (dort steht der
  // erste Reiter buendig zur Zeilenkante: Text ab 301, `Tab`-Polster 16 →
  // Kasten 285). Belegt ist in beiden Messungen dasselbe: die Zeile sass
  // links vom Titel (156 bzw. 116 px).
  //
  // Der Deckel des Kopfes ist NICHT nachgebaut: `ViewHeader` fuehrt ihn selbst
  // (`musterDeckel`; auf `full`-Seiten `CONTENT_WIDTH.reading`) und folgt einer
  // gemeldeten Tabellenbreite gestuft (G0b.4). Die Zeile im Slot folgt beidem
  // mit, weil sie im selben Kasten sitzt. Der INHALT bleibt deckelfrei.

  // Ein Menueeintrag ist ein LINK, nicht ein Klickziel mit Zustand: der
  // Abschnitt hat eine Adresse, Strg/Cmd-Klick oeffnet sie in einem neuen Tab.
  // Der aktuelle Eintrag ist nicht nur gefaerbt (`selected`), sondern traegt
  // ein Haekchen und `aria-current="page"` (R16-W4: „Status ist nie nur Farbe").
  const menueEintrag = (s) => (
    <MenuItem
      key={s.key}
      component={RouterLink}
      to={s.path}
      selected={s.key === active}
      aria-current={s.key === active ? 'page' : undefined}
      onClick={() => setNavAnchor(null)}
    >
      {s.label}
      {s.key === active && <CheckIcon fontSize="small" sx={{ ml: 'auto' }} />}
    </MenuItem>
  )

  return (
    <PageContainer width={width}>
      {/* Header — the SHARED ViewHeader, so top spacing, indent and heading
          hierarchy match every other page (R16 W1/W4). „Admin" is the eyebrow
          (breadcrumb, first crumb linked back to /admin); the section name is
          the h1, because that is what identifies the page.
          🔴 Nachgezogen 2026-10-07 (B5, UI-Durchgang 2): die Statuspille sitzt
          jetzt im `titleAdornment` HINTER DEM TITEL, nicht mehr als rechte
          Kopfaktion. Der Grund ist die Form, nicht die Sichtbarkeit: im
          Aktionsbereich stand sie zwischen Knöpfen und sah selbst wie einer
          aus (Fotobefund 2026-10-07: gefüllte Pille unmittelbar neben
          „Edit prices" outlined). Sichtbar bleibt sie unverändert — auch beim
          Scrollen langer Listen, weil sie an der Titelzeile hängt und nicht
          am Listeninhalt.
          🔴 R16-W1 (2026-10-28): die Navigationszeile bleibt UNTER dem Kopf.
          Das Konzept (§5.1) legt sie für die Seitennavigation darüber; hier
          bleibt sie darunter, weil das der Bestand ist (kein Rückschritt) und
          weil mobil Titel und Auswähler in derselben Reihenfolge stehen —
          eine Umhängung wäre eine reine Formänderung ohne Bediengewinn. */}
      <ViewHeader
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: sectionLabel }]}
        title={
          <Typography
            component="h1"
            variant={narrow ? 'h6' : 'h5'}
            noWrap
            sx={{ fontWeight: narrow ? 700 : 600 }}
          >
            {sectionLabel}
          </Typography>
        }
        titleAdornment={<StatusPill status={summary?.status || 'loading'} compact={narrow} />}
      >
        {/* Die Navigationszeile sitzt im `children`-Slot des Kopfes (R16-W2):
            derselbe Kasten wie der Titel, dieselbe linke Kante — ohne zweite
            Rechnung. Der Slot rendert unter der Titelzeile, also bleibt die
            Zeile unter dem Kopf (R16-W1). */}
        <Box data-testid="admin-navigation">
          {isMobile ? (
            /* Navigation, nicht Filter (P31/N5.6): ein Knopf mit Bereichsname,
               aktuellem Abschnitt und Navigationssymbol, der ein Menue oeffnet.
               Kein `Select` — der traegt `role="combobox"` und las sich wie ein
               Filter. Das Aufklapp-Zeichen ist sichtbar (R16-W4), nicht nur
               `aria-haspopup`. */
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AppsIcon />}
                endIcon={<ExpandMoreIcon />}
                aria-haspopup="menu"
                aria-expanded={Boolean(navAnchor)}
                onClick={(e) => setNavAnchor(e.currentTarget)}
              >
                {`Admin · ${sectionLabel}`}
              </Button>
              <Menu
                anchorEl={navAnchor}
                open={Boolean(navAnchor)}
                onClose={() => setNavAnchor(null)}
              >
                {PRIMARY_SECTIONS.map(menueEintrag)}
                <Divider />
                {SECONDARY_SECTIONS.map(menueEintrag)}
              </Menu>
            </>
          ) : (
            /* Zone 3 — Reiter. `data-testid="liste-reiter"` umschliesst NUR die
               Reihe, nicht den Inhalt: an jeder anderen Stelle im Repo bedeutet
               die Marke „hier beginnt die Reiterleiste" (`POInbox.jsx`,
               `ListenMuster.jsx`). Der Inhalt liegt DANEBEN. */
            <Box data-testid="liste-reiter">
              <Tabs
                ref={tabsRef}
                value={secondaryActive ? MORE_KEY : active}
                variant="scrollable"
                scrollButtons="auto"
                // R16-C1 (2026-10-29): der `role="tablist"` war unbenannt — ein
                // Screenreader sagte „Registerkarte, 1 von 9", ohne zu sagen,
                // wovon. Der Name stand bis G14a-0 am `UnderlineTabs`-Baustein
                // (`ariaLabel="Admin sections"`) und ging beim Wechsel auf MUI
                // `Tabs` verloren.
                aria-label="Admin sections"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                {PRIMARY_SECTIONS.map(s => (
                  <Tab
                    key={s.key}
                    value={s.key}
                    label={s.label}
                    component={RouterLink}
                    to={s.path}
                    aria-current={s.key === active ? 'page' : undefined}
                  />
                ))}
                <Tab
                  value={MORE_KEY}
                  // Der aktuelle Abschnitt steht SICHTBAR im Reiter, wenn er
                  // unter „More" liegt — sonst waere der aktuelle Ort auf dem
                  // Desktop nicht ablesbar.
                  label={secondaryActive ? sectionLabel : 'More'}
                  icon={<ExpandMoreIcon fontSize="small" />}
                  iconPosition="end"
                  aria-haspopup="menu"
                  aria-expanded={Boolean(moreAnchor)}
                  aria-current={secondaryActive ? 'page' : undefined}
                  onClick={(e) => setMoreAnchor(e.currentTarget)}
                />
              </Tabs>
            </Box>
          )}
        </Box>
      </ViewHeader>

      {/* Der Inhalt haengt EINMAL im Baum, ausserhalb der Verzweigung oben
          (Regel-15-Bedingung 1: kein Neu-Einhaengen beim Breakpoint-Wechsel).
          Er bleibt deckelfrei (R16-W2): `full`-Seiten tragen keinen Deckel. */}
      <Box sx={{ width: '100%', mt: SP.section }}>
        <Outlet />
      </Box>

      <Menu
        anchorEl={moreAnchor}
        open={Boolean(moreAnchor)}
        onClose={() => setMoreAnchor(null)}
      >
        {SECONDARY_SECTIONS.map(s => (
          <MenuItem
            key={s.key}
            component={RouterLink}
            to={s.path}
            selected={s.key === active}
            aria-current={s.key === active ? 'page' : undefined}
            onClick={() => setMoreAnchor(null)}
          >
            {s.label}
            {s.key === active && <CheckIcon fontSize="small" sx={{ ml: 'auto' }} />}
          </MenuItem>
        ))}
      </Menu>
    </PageContainer>
  )
}
